/** @jsx jsx */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Input, jsx } from 'theme-ui'

const StoreLocatorSearch = ({
  map,
  setCurrentCenter,
  setError,
  styles,
  ui,
}) => {
  const autocompleteInput = useRef(null)
  const autocompleteButton = useRef(null)
  const [autocomplete, setAutocomplete] = useState(null)
  const [geocoder, setGeocoder] = useState(null)

  // Updates the current center from a new search.
  const handleSearch = useCallback(async () => {
    let place = autocomplete.getPlace()

    // Geocode input when enter key or submit button is used.
    if ((!place || !place.geometry) && geocoder && map) {
      place = await geocoder
        .geocode({
          address: place?.name || autocompleteInput.current.value,
          bounds: map.getBounds(),
        })
        .then((response) => response?.results?.[0])
        .catch((error) => setError("Sorry, we couldn't find that address."))
    }

    if (!place) return

    if (place.formatted_address) {
      autocompleteInput.current.value = place.formatted_address
    }

    setError(null)
    setCurrentCenter({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    })
    map.setZoom(12)
  }, [autocomplete, geocoder, map]) // eslint-disable-line

  // Initializes the autocomplete search.
  useEffect(() => {
    if (autocomplete || !autocompleteInput.current) return

    setAutocomplete(
      new window.google.maps.places.Autocomplete(autocompleteInput.current, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'geometry', 'name'],
        strictBounds: false,
        types: ['address'],
      })
    )
  }, [autocompleteInput]) // eslint-disable-line

  // Initializes the geocoder used by handleSearch().
  useEffect(() => {
    setGeocoder(new window.google.maps.Geocoder())
  }, []) // eslint-disable-line

  useEffect(() => {
    if (!autocomplete || !map) return

    // Adds the autocomplete event listener.
    autocomplete.addListener('place_changed', handleSearch)

    // Biases autocomplete results to map boundaries.
    autocomplete.bindTo('bounds', map)
  }, [autocomplete, map]) // eslint-disable-line

  return (
    <Box sx={{ display: 'flex', ...styles?.search?.container }}>
      <Box
        sx={{
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          height: '1px',
          overflow: 'hidden',
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: '1px',
        }}
      >
        <label htmlFor="store-locator-search">{'Search locations'}</label>
      </Box>
      <Input
        id="store-locator-search"
        ref={autocompleteInput}
        type="text"
        p={2}
        placeholder={ui?.search?.input?.placeholder}
        sx={styles?.search?.input}
      />
      <Button
        ref={autocompleteButton}
        aria-label="Search locations"
        onClick={() => handleSearch()}
        sx={{ flexShrink: 0, ml: 1, ...styles?.search?.button }}
      >
        {ui?.search?.button?.label || 'Search'}
      </Button>
    </Box>
  )
}

export default StoreLocatorSearch
